import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { Box, H, Text, Flex, DropdownOptions, TooltipPopup } from 'dash-ui';
import Header from 'flow-components/Header/Header';
import Footer from 'flow-components/Footer/Footer';
import { RoutesPath } from 'flow-components/routes/routes';
import Container from '../Container/Container';
import { useBrowserTabTitle } from 'common/hooks/use-browser-tab-title/use-browser-tab-title';
import { useTranslation } from 'react-i18next';

function TermConditions() {
  const { t } = useTranslation(['pages']);
  const theme = useTheme();
  useBrowserTabTitle(
    `${t('common:labels.browserTabTitles.termConditions')} ${t(
      'common:labels.dashSuffix'
    )}`
  );
  return (
    <Box h="100%">
      <DropdownOptions />
      <TooltipPopup />
      <Header withProfileDropdown={false} />
      <Container withSidebar={false}>
        <Flex flexDirection="column">
          <H lg> Platform Terms of Service </H>
          <H sm>Last modified: June 21, 2023</H>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Acceptance of the Platform Terms of Service
            </Text>
            <Text color={theme.colors.n80}>
              This Platform (defined below) being offered by DASH ("
              <Text as="span" semibold color={theme.colors.n80}>
                Company
              </Text>
              ," "
              <Text as="span" semibold color={theme.colors.n80}>
                we
              </Text>
              ," or "
              <Text as="span" semibold color={theme.colors.n80}>
                us
              </Text>
              ") provides real estate agents with access to new development
              content exclusive to pre- and new-development projects. These
              terms of service for the Platform are entered into by and between
              you and us. The following terms and conditions, together with any
              documents they expressly incorporate by reference (collectively, "
              <Text as="span" semibold color={theme.colors.n80}>
                Platform Terms of Service
              </Text>
              "), govern your access to and use of the Company’s platform
              accessed at{' '}
              <a href="https://dashtodash.com">https://dashtodash.com</a>,
              including any content, functionality, and services offered on or
              through the Company’s Platform (the "
              <Text as="span" semibold color={theme.colors.n80}>
                Platform
              </Text>
              "), whether as a guest or a registered user. <br />
              <Text as="span" semibold color={theme.colors.n80}>
                Please read the Platform Terms of Service carefully before you
                start to use the Platform. By clicking “AGREE”, you accept and
                agree to be bound and abide by these Platform Terms of Use and
                our Privacy Policy, found at&nbsp;
                <Link target="_blank" to={RoutesPath.PrivacyPolicy}>
                  https://dashtodash.com/privacy-policy/
                </Link>
                , incorporated herein by reference. If you do not want to agree
                to these Platform Terms of Service or the&nbsp;
                <Link target="_blank" to={RoutesPath.PrivacyPolicy}>
                  Privacy Policy
                </Link>
                , you must not access or use the Platform.
              </Text>
              <br />
              If you are accessing or using this Platform and are under the
              legal age to be responsible for your actions (i.e. if you are
              under age 18), then your parent or guardian must email us
              immediately at support@dashtodash.com before you continue, and
              must grant us express permission for you to access and use this
              Platform, including to use any services provided through or in
              connection with the Platform.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              The Platform
            </Text>
            <Text color={theme.colors.n80}>
              In order to use the Platform, you must create an account. You are
              asked to provide your name, email address, phone number, agent
              license number, and geolocation information; provision of your
              paction, biographical information and company details are options.
              The provision and use of this information is governed by our
              Privacy Policy.
              <br />
              After you have created an account, you will be asked to select a
              subscription plan: either a monthly plan, or a yearly recurring
              plan. Each plan offers a 10-day trial period. You will then be
              requested to provide your credit card information via Stripe, but
              will not be charged until the end of the trial period. You will
              have the ability to cancel your subscription at any time (subject
              to being charged through the end of the period you have selected).
              Invoices for your subscription plan are downloadable via the
              Platform. All charging and other interactions with Stripe are
              subject to Stripe’s terms and conditions, found at
              https://stripe.com/legal/end-users. We expressly disclaim all
              liability for all of Stripe’s acts and omissions.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Changes to the Platform Terms of Service
            </Text>
            <Text color={theme.colors.n80}>
              We may revise and update these Platform Terms of Service from time
              to time in our sole discretion. All changes are effective
              immediately when we post them, and apply to all access to and use
              of the Platform thereafter.
              <br />
              Your continued use of the Platform following the posting of
              revised Platform Terms of Use means that you accept and agree to
              the changes. You are expected to check this page each time you
              access this Platform so you are aware of any changes, as they are
              binding on you.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Accessing the Platform and Account Security
            </Text>
            <Text color={theme.colors.n80}>
              We reserve the right to withdraw or amend this Platform, and any
              service or material we provide on the Platform, in our sole
              discretion without notice. We will not be liable if for any reason
              all or any part of the Platform is unavailable at any time or for
              any period. From time to time, we may restrict access to some
              parts of the Platform, or the entire Platform, to users, including
              registered users.
              <br />
              You are responsible for both: <br />
              <ul>
                <li>
                  Making all arrangements necessary for you to have access to
                  the Platform.
                </li>
                <li>
                  Ensuring that all persons who access the Platform through your
                  internet connection are aware of these Platform Terms of
                  Service and comply with them.
                </li>
              </ul>
              To access the Platform or some of the resources it offers, you may
              be asked to provide certain registration details or other
              information. It is a condition of your use of the Platform that
              all the information you provide on the Platform is correct,
              current, and complete. You agree that all information you provide
              to register with this Platform or otherwise, including, but not
              limited to, through the use of any interactive features on the
              Website, is governed by our&nbsp;
              <Link target="_blank" to={RoutesPath.PrivacyPolicy}>
                Privacy Policy
              </Link>
              , and you consent to all actions we take with respect to your
              information consistent with our&nbsp;
              <Link target="_blank" to={RoutesPath.PrivacyPolicy}>
                Privacy Policy
              </Link>
              .
              <br />
              If you choose, or are provided with, a user name, password, or any
              other piece of information as part of our security procedures, you
              must treat such information as confidential, and you must not
              disclose it to any other person or entity. You also acknowledge
              that your account is personal to you and agree not to provide any
              other person with access to this Platform or portions of it using
              your user name, password, or other security information. You agree
              to notify us immediately of any unauthorized access to or use of
              your user name or password or any other breach of security. You
              also agree to ensure that you exit from your account at the end of
              each session. You should use particular caution when accessing
              your account from a public or shared computer so that others are
              not able to view or record your password or other personal
              information.
              <br />
              We have the right to disable any user name, password, or other
              identifier, whether chosen by you or provided by us, at any time
              in our sole discretion for any or no reason, including if, in our
              opinion, you have violated any provision of these Platform Terms
              of Service.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Intellectual Property Rights
            </Text>
            <Text color={theme.colors.n80}>
              The Platform and its entire contents, features, and functionality
              (including but not limited to all information, software, text,
              displays, images, video, and audio, and the design, selection, and
              arrangement thereof) are owned by the Company, its licensors, or
              other providers of such material and are protected by United
              States and international copyright, trademark, patent, trade
              secret, and other intellectual property or proprietary rights
              laws.
              <br />
              These Platform Terms of Service permit you to use the Platform for
              your individual commercial use. You must not reproduce,
              distribute, modify, create derivative works of, publicly display,
              publicly perform, republish, download, store, or transmit any of
              the material on our Platform, except as follows:
              <br />
              <ul>
                <li>
                  Your computer may temporarily store copies of such materials
                  in RAM incidental to your accessing and viewing those
                  materials.
                </li>
                <li>
                  You may store files that are automatically cached by your Web
                  browser for display enhancement purposes.
                </li>
                <li>
                  You may store files that are automatically cached by your Web
                  browser for display enhancement purposes.
                </li>
                <li>
                  You may print or download one copy of a reasonable number of
                  pages of the Website for your business purposes contemplated
                  by the Platform, but not for other reproduction, publication,
                  or distribution.
                </li>
                <li>
                  If we provide desktop, mobile, or other applications for
                  download, you may download a single copy to your computer or
                  mobile device solely for your commercial use, provided you
                  agree to be bound by our end user license agreement for such
                  applications.
                </li>
              </ul>
              Without our permission, you must not:
              <br />
              <ul>
                <li>Modify copies of any materials from the Platform.</li>
                <li>
                  Use any illustrations, photographs, video or audio sequences,
                  or any graphics separately from the accompanying text.
                </li>
                <li>
                  Delete or alter any copyright, trademark, or other proprietary
                  rights notices from copies of materials from the Platform.
                </li>
              </ul>
              If you wish to make any use of material on the Platform other than
              that set out in this section, please address your request to:
              support@dashtodash.com.
              <br />
              If you print, copy, modify, download, or otherwise use or provide
              any other person with access to any part of the Platform in breach
              of the Platform Terms of Service, your right to use the Platform
              will stop immediately and you must, at our option, return or
              destroy any copies of the materials you have made. No right,
              title, or interest in or to the Platform or any content on the
              Platform is transferred to you, and all rights not expressly
              granted are reserved by the Company. Any use of the Platform not
              expressly permitted by these Platform Terms of Service is a breach
              of these Platform Terms of Service and may violate copyright,
              trademark, and other laws.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Trademarks
            </Text>
            <Text color={theme.colors.n80}>
              The Company name, [the Company logo,] and all related names,
              logos, product and service names, designs, and slogans are
              trademarks of the Company or its affiliates or licensors. You must
              not use such marks without the prior written permission of the
              Company. All other names, logos, product and service names,
              designs, and slogans on this Platform are the trademarks of their
              respective owners.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Prohibited Uses
            </Text>
            <Text color={theme.colors.n80}>
              You may use the Platform only for lawful purposes and in
              accordance with these Platform Terms of Service. You agree not to
              use the Platform:
              <br />
              <ul>
                <li>
                  In any way that violates any applicable federal, state, local,
                  or international law or regulation (including, without
                  limitation, any laws regarding the export of data or software
                  to and from the US or other countries).
                </li>
                <li>
                  For the purpose of exploiting, harming, or attempting to
                  exploit or harm minors in any way by exposing them to
                  inappropriate content, asking for personally identifiable
                  information, or otherwise.
                </li>
                <li>
                  To transmit, or procure the sending of, any advertising or
                  promotional material, including any "junk mail," "chain
                  letter," "spam," or any other similar solicitation.
                </li>
                <li>
                  To impersonate or attempt to impersonate the Company, a
                  Company employee, another user, or any other person or entity
                  (including, without limitation, by using email addresses or
                  screen names associated with any of the foregoing).
                </li>
                <li>
                  To engage in any other conduct that restricts or inhibits
                  anyone's use or enjoyment of the Platform, or which, as
                  determined by us, may harm the Company or users of the
                  Platform, or expose them to liability.
                </li>
              </ul>
              Additionally, you agree not to:
              <br />
              <ul>
                <li>
                  Use the Website in any manner that could disable, overburden,
                  damage, or impair the Platform or interfere with any other
                  party's use of the Platform, including their ability to engage
                  in real time activities through the Platform.
                </li>
                <li>
                  Use any robot, spider, or other automatic device, process, or
                  means to access the Platform for any purpose.
                </li>
                <li>
                  Use any manual process to monitor or copy any of the material
                  on the Platform, or for any other purpose not expressly
                  authorized in these Platform Terms of Service, without our
                  prior written consent.
                </li>
                <li>
                  Use any device, software, or routine that interferes with the
                  proper working of the Platform.
                </li>
                <li>
                  Introduce any viruses, Trojan horses, worms, logic bombs, or
                  other material that is malicious or technologically harmful.
                </li>
                <li>
                  Attempt to gain unauthorized access to, interfere with,
                  damage, or disrupt any parts of the Platform, the server on
                  which the Platform is stored, or any server, computer, or
                  database connected to the Platform.
                </li>
                <li>
                  Attack the Platform via a denial-of-service attack or a
                  distributed denial-of-service attack.
                </li>
                <li>
                  Otherwise attempt to interfere with the proper working of the
                  Platform.
                </li>
              </ul>
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Reliance on Information Posted
            </Text>
            <Text color={theme.colors.n80}>
              The information presented on or through the Platform is made
              available solely for general information purposes. We do not
              warrant the accuracy, completeness, or usefulness of this
              information. Any reliance you place on such information is
              strictly at your own risk. We disclaim all liability and
              responsibility arising from any reliance placed on such materials
              on the Platform, or by anyone who may be informed of any of its
              contents. This Platform may include content provided by third
              parties, including materials provided by other third-party
              licensors, syndicators, aggregators, and/or reporting services.
              All statements and/or opinions expressed in these materials, and
              all articles and responses to questions and other content, other
              than the content provided by the Company, are solely the opinions
              and the responsibility of the person or entity providing those
              materials. These materials do not necessarily reflect the opinion
              of the Company. We are not responsible, or liable to you or any
              third party, for the content or accuracy of any materials provided
              by any third parties.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Changes to the Platform
            </Text>
            <Text color={theme.colors.n80}>
              We may update the content on this Platform from time to time, but
              its content is not necessarily complete or up-to-date. Any of the
              material on the Platform may be out of date at any given time, and
              we are under no obligation to update such material.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Information About You and Your Visits to the Platform
            </Text>
            <Text color={theme.colors.n80}>
              All information we collect on this Platform is subject to our
              Platform&nbsp;
              <Link target="_blank" to={RoutesPath.PrivacyPolicy}>
                Privacy Policy
              </Link>
              . By using the Platform, you consent to all actions taken by us
              with respect to your information in compliance with the
              Platform&nbsp;
              <Link target="_blank" to={RoutesPath.PrivacyPolicy}>
                Privacy Policy
              </Link>
              .
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Links from the Platform
            </Text>
            <Text color={theme.colors.n80}>
              If the Platform contains links to other sites and resources
              provided by third parties, these links are provided for your
              convenience only. This includes links contained in advertisements,
              including banner advertisements and sponsored links. We have no
              control over the contents of those sites or resources, and accept
              no responsibility for them or for any loss or damage that may
              arise from your use of them. If you decide to access any of the
              third-party websites linked to this Platform, you do so entirely
              at your own risk and subject to the terms and conditions of use
              for such websites.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Geographic Restrictions
            </Text>
            <Text color={theme.colors.n80}>
              The owner of the Platform is based in the State of Florida in the
              United States. We provide this Platform for use only by persons
              located in the United States. We make no claims that the Platform
              or any of its content is accessible or appropriate outside of the
              United States. Access to the Platform may not be legal by certain
              persons or in certain countries. If you access the Platform from
              outside the United States, you do so on your own initiative and
              are responsible for compliance with local laws.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Disclaimer of Warranties
            </Text>
            <Text color={theme.colors.n80}>
              You understand that we cannot and do not guarantee or warrant that
              files available for downloading from the internet or the Platform
              will be free of viruses or other destructive code. You are
              responsible for implementing sufficient procedures and checkpoints
              to satisfy your particular requirements for anti-virus protection
              and accuracy of data input and output, and for maintaining a means
              external to our site for any reconstruction of any lost data. TO
              THE FULLEST EXTENT PROVIDED BY LAW, WE WILL NOT BE LIABLE FOR ANY
              LOSS OR DAMAGE CAUSED BY A DISTRIBUTED DENIAL-OF-SERVICE ATTACK,
              VIRUSES, OR OTHER TECHNOLOGICALLY HARMFUL MATERIAL THAT MAY INFECT
              YOUR COMPUTER EQUIPMENT, COMPUTER PROGRAMS, DATA, OR OTHER
              PROPRIETARY MATERIAL DUE TO YOUR USE OF THE WEBSITE OR ANY
              SERVICES OR ITEMS OBTAINED THROUGH THE PLATFORM OR TO YOUR
              DOWNLOADING OF ANY MATERIAL POSTED ON IT, OR ON ANY WEBSITE LINKED
              TO IT.
              <br />
              YOUR USE OF THE PLATFORM, ITS CONTENT, AND ANY SERVICES OR ITEMS
              OBTAINED THROUGH THE PLATFORM IS AT YOUR OWN RISK. THE PLATFORM,
              ITS CONTENT, AND ANY SERVICES OR ITEMS OBTAINED THROUGH THE
              PLATFORM ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS,
              WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
              NEITHER THE COMPANY NOR ANY PERSON ASSOCIATED WITH THE COMPANY
              MAKES ANY WARRANTY OR REPRESENTATION WITH RESPECT TO THE
              COMPLETENESS, SECURITY, RELIABILITY, QUALITY, ACCURACY, OR
              AVAILABILITY OF THE PLATFORM. WITHOUT LIMITING THE FOREGOING,
              NEITHER THE COMPANY NOR ANYONE ASSOCIATED WITH THE COMPANY
              REPRESENTS OR WARRANTS THAT THE PLATFORM, ITS CONTENT, OR ANY
              SERVICES OR ITEMS OBTAINED THROUGH THE PLATFORM WILL BE ACCURATE,
              RELIABLE, ERROR-FREE, OR UNINTERRUPTED, THAT DEFECTS WILL BE
              CORRECTED, THAT THE PLATFORM OR THE SERVER THAT MAKES IT AVAILABLE
              ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS, OR THAT THE
              PLATFORM OR ANY SERVICES OR ITEMS OBTAINED THROUGH THE PLATFORM
              WILL OTHERWISE MEET YOUR NEEDS OR EXPECTATIONS.
              <br />
              TO THE FULLEST EXTENT PROVIDED BY LAW, THE COMPANY HEREBY
              DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED,
              STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO ANY
              WARRANTIES OF MERCHANTABILITY, NON-INFRINGEMENT, AND FITNESS FOR
              PARTICULAR PURPOSE.
              <br />
              THE FOREGOING DOES NOT AFFECT ANY WARRANTIES THAT CANNOT BE
              EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
              <br />
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Limitation on Liability
            </Text>
            <Text color={theme.colors.n80}>
              TO THE FULLEST EXTENT PROVIDED BY LAW, IN NO EVENT WILL THE
              COMPANY, ITS AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS,
              EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR DAMAGES OF
              ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION
              WITH YOUR USE, OR INABILITY TO USE, THE PLATFORM, ANY WEBSITES
              LINKED TO IT, ANY CONTENT ON THE PLATFORM OR SUCH OTHER WEBSITES,
              INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL,
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO,
              PERSONAL INJURY, PAIN AND SUFFERING, EMOTIONAL DISTRESS, LOSS OF
              REVENUE, LOSS OF PROFITS, LOSS OF BUSINESS OR ANTICIPATED SAVINGS,
              LOSS OF USE, LOSS OF GOODWILL, LOSS OF DATA, AND WHETHER CAUSED BY
              TORT (INCLUDING NEGLIGENCE), BREACH OF CONTRACT, OR OTHERWISE,
              EVEN IF FORESEEABLE.
              <br />
              THE FOREGOING DOES NOT AFFECT ANY LIABILITY THAT CANNOT BE
              EXCLUDED OR LIMITED UNDER APPLICABLE LAW.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Indemnification
            </Text>
            <Text color={theme.colors.n80}>
              You agree to defend, indemnify, and hold harmless the Company, its
              affiliates, licensors, and service providers, and its and their
              respective officers, directors, employees, contractors, agents,
              licensors, suppliers, successors, and assigns from and against any
              claims, liabilities, damages, judgments, awards, losses, costs,
              expenses, or fees (including reasonable attorneys' fees) arising
              out of or relating to your violation of these Platform Terms of
              Service or your use of the Platform, including, but not limited
              to, any use of the Platform's content, services, and products
              other than as expressly authorized in these Platform Terms of
              Service, or your use of any information obtained from the
              Platform.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Governing Law and Jurisdiction
            </Text>
            <Text color={theme.colors.n80}>
              All matters relating to the Platform and these Platform Terms of
              Service, and any dispute or claim arising therefrom or related
              thereto (in each case, including non-contractual disputes or
              claims), shall be governed by and construed in accordance with the
              internal laws of the State of Florida without giving effect to any
              choice or conflict of law provision or rule (whether of the State
              of Florida or any other jurisdiction).
              <br />
              Any legal suit, action, or proceeding arising out of, or related
              to, these Platform Terms of Service or the Platform shall be
              instituted exclusively in the federal courts of the United States
              or the courts of the State of Florida although we retain the right
              to bring any suit, action, or proceeding against you for breach of
              these Platform Terms of Service in your country of residence or
              any other relevant country. You waive any and all objections to
              the exercise of jurisdiction over you by such courts and to venue
              in such courts.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Limitation on Time to File Claims
            </Text>
            <Text color={theme.colors.n80}>
              ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE ARISING OUT OF OR
              RELATING TO THESE TERMS OF USE OR THE WEBSITE MUST BE COMMENCED
              WITHIN ONE (1) YEAR AFTER THE CAUSE OF ACTION ACCRUES; OTHERWISE,
              SUCH CAUSE OF ACTION OR CLAIM IS PERMANENTLY BARRED.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Waiver and Severability
            </Text>
            <Text color={theme.colors.n80}>
              No waiver by the Company of any term or condition set out in these
              Platform Terms of Service shall be deemed a further or continuing
              waiver of such term or condition or a waiver of any other term or
              condition, and any failure of the Company to assert a right or
              provision under these Platform Terms of Service shall not
              constitute a waiver of such right or provision.
              <br />
              If any provision of these Platform Terms of Service is held by a
              court or other tribunal of competent jurisdiction to be invalid,
              illegal, or unenforceable for any reason, such provision shall be
              eliminated or limited to the minimum extent such that the
              remaining provisions of the Platform Terms of Service will
              continue in full force and effect.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Entire Agreement
            </Text>
            <Text color={theme.colors.n80}>
              The Platform Terms of Use, our&nbsp;
              <Link target="_blank" to={RoutesPath.PrivacyPolicy}>
                Privacy Policy
              </Link>
              , our Terms of Use and any documents incorporated by reference
              therein constitute the sole and entire agreement between you and
              the Company regarding the Platform and supersede all prior and
              contemporaneous understandings, agreements, representations, and
              warranties, both written and oral, regarding the Platform.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Your Comments and Concerns
            </Text>
            <Text color={theme.colors.n80}>
              This Platform is operated by the Company at the following
              principal place of business:
              <br />
              DASH, Inc.
              <br />
              1000 Venetian Way #2202
              <br />
              Miami Beach, FL 33139
              <br />
              All other feedback, comments, requests for technical support, and
              other communications relating to the Platform should be directed
              to: support@dashtodash.com.
            </Text>
          </Flex>
        </Flex>
      </Container>
      <Footer variation="dark" />
    </Box>
  );
}

export default TermConditions;
